import React, { useState, useRef, useEffect } from 'react';
import { toPng} from 'html-to-image';
import jsPDF from 'jspdf';
import CuadranteGrid from './CuadranteGrid';
import CuadranteDataGrid from './CuadranteDataGrid';
import './DisplayCuadrante.scss';

const DisplayCuadrante = ({ cuadrantes, tipoConsulta, año, mes }) => {
  const [selectedCuadrante, setSelectedCuadrante] = useState(null);
  const [exportando, setExportando] = useState(false);
  // Si es búsqueda por persona, iniciar en vista lista (no tiene días específicos)
  // Si es búsqueda por contrato, iniciar en vista grid (tiene días específicos)
  const [vistaActual, setVistaActual] = useState(tipoConsulta === 'persona' ? 'lista' : 'grid');
  const cuadranteRef = useRef(null);

  // Cambiar vista automáticamente cuando cambie el tipo de consulta
  useEffect(() => {
    setVistaActual(tipoConsulta === 'persona' ? 'lista' : 'grid');
  }, [tipoConsulta]);

  // Log para debug
  useEffect(() => {
    if (cuadrantes && cuadrantes.length > 0) {
      console.log('📊 DisplayCuadrante - Datos recibidos:', {
        cantidad: cuadrantes.length,
        tipoConsulta,
        primerItem: cuadrantes[0]
      });
    }
  }, [cuadrantes, tipoConsulta]);

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    try {
      // Si es un número como 20120401, convertirlo a fecha
      if (typeof fecha === 'number') {
        const fechaStr = fecha.toString();
        if (fechaStr.length === 8) {
          const año = fechaStr.substring(0, 4);
          const mes = fechaStr.substring(4, 6);
          const dia = fechaStr.substring(6, 8);
          return `${dia}/${mes}/${año}`;
        }
      }
      const date = new Date(fecha);
      if (isNaN(date.getTime())) {
        return fecha.toString();
      }
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return fecha.toString();
    }
  };

  const formatearMes = (mes) => {
    const meses = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    const mesNum = typeof mes === 'number' ? mes : parseInt(mes);
    if (isNaN(mesNum) || mesNum < 1 || mesNum > 12) {
      return mes;
    }
    return meses[mesNum - 1] || mes;
  };

  const exportarAPDF = async (cuadrante) => {
    try {
      setExportando(true);
      console.log('📄 Iniciando exportación a PDF...');

      // Esperar un momento para que el DOM se actualice
      await new Promise(resolve => setTimeout(resolve, 100));

      const element = cuadranteRef.current;
      if (!element) {
        throw new Error('No se pudo encontrar el elemento del cuadrante');
      }

      // Capturar el elemento como imagen PNG
      const dataUrl = await toPng(element, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#ffffff'
      });

      // Crear una imagen para obtener dimensiones
      const img = new Image();
      img.src = dataUrl;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      // Calcular dimensiones para el PDF
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (img.height * imgWidth) / img.width;
      
      // Crear PDF
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Si la imagen es muy alta, dividir en varias páginas
      if (imgHeight > 297) {
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= 297;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= 297;
        }
      } else {
        pdf.addImage(dataUrl, 'PNG', 0, 0, imgWidth, imgHeight);
      }

      // Generar nombre del archivo
      const nombrePersona = cuadrante.VSSPER_NOM && cuadrante.VSSPER_APELL1 
        ? `${cuadrante.VSSPER_NOM}_${cuadrante.VSSPER_APELL1}`.replace(/\s+/g, '_')
        : 'cuadrante';
      const mes = formatearMes(cuadrante.CUADT_MES);
      const año = cuadrante.CUADT_CAL;
      const nombreArchivo = `Cuadrante_${nombrePersona}_${mes}_${año}.pdf`;

      // Descargar el PDF
      pdf.save(nombreArchivo);
      console.log('✅ PDF exportado correctamente:', nombreArchivo);
    } catch (error) {
      console.error('❌ Error al exportar PDF:', error);
      alert('Error al exportar el PDF. Por favor, inténtalo de nuevo.');
    } finally {
      setExportando(false);
    }
  };

  const agruparPorPersona = () => {
    const agrupado = {};
    cuadrantes.forEach(cuadrante => {
      const key = cuadrante.CUADT_PERSONA;
      if (!agrupado[key]) {
        agrupado[key] = [];
      }
      agrupado[key].push(cuadrante);
    });
    return agrupado;
  };

  const renderCuadranteDetalle = (cuadrante) => (
    <div className="cuadrante-detalle" ref={cuadranteRef}>
      <div className="cuadrante-header">
        <h2>Cuadrante de Trabajo</h2>
        <div className="periodo">
          {formatearMes(cuadrante.CUADT_MES)} {cuadrante.CUADT_CAL}
        </div>
      </div>

      <div className="info-grid">
        {cuadrante.VSSPER_NOM && (
          <div className="info-section personal">
            <h3>👤 Información Personal</h3>
            <div className="info-row">
              <span className="label">Nombre:</span>
              <span className="value">
                {cuadrante.VSSPER_NOM} {cuadrante.VSSPER_APELL1} {cuadrante.VSSPER_APELL2}
              </span>
            </div>
            {cuadrante.VSSPER_NIF && (
              <div className="info-row">
                <span className="label">NIF:</span>
                <span className="value">{cuadrante.VSSPER_NIF}{cuadrante.VSSPER_NIF_DC}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Código Persona:</span>
              <span className="value">{cuadrante.CUADT_PERSONA}</span>
            </div>
          </div>
        )}

        <div className="info-section contrato">
          <h3>📄 Información del Contrato</h3>
          <div className="info-row">
            <span className="label">Empresa:</span>
            <span className="value">{cuadrante.CUADT_EMP}</span>
          </div>
          <div className="info-row">
            <span className="label">Delegación:</span>
            <span className="value">{cuadrante.CUADT_DELEG}</span>
          </div>
          <div className="info-row">
            <span className="label">Contrato:</span>
            <span className="value">{cuadrante.CUADT_CTRT}</span>
          </div>
          <div className="info-row">
            <span className="label">Servicio:</span>
            <span className="value">{cuadrante.CUADT_SERV}</span>
          </div>
        </div>

        <div className="info-section horas">
          <h3>⏰ Resumen de Horas</h3>
          <div className="info-row">
            <span className="label">Turno/Operación:</span>
            <span className="value">{cuadrante.CUADT_TURNOPER || 'N/A'}</span>
          </div>
          <div className="info-row">
            <span className="label">Horas Totales:</span>
            <span className="value highlight">{cuadrante.CUADT_HHTOT || 0} h</span>
          </div>
          {cuadrante.CUADT_HHTOTB && (
            <div className="info-row">
              <span className="label">Horas Base:</span>
              <span className="value">{cuadrante.CUADT_HHTOTB} h</span>
            </div>
          )}
          {cuadrante.CUADT_HHFEST !== undefined && cuadrante.CUADT_HHFEST > 0 && (
            <div className="info-row">
              <span className="label">Horas Festivas:</span>
              <span className="value">{cuadrante.CUADT_HHFEST} h</span>
            </div>
          )}
          {cuadrante.CUADT_HHNOC !== undefined && cuadrante.CUADT_HHNOC > 0 && (
            <div className="info-row">
              <span className="label">Horas Nocturnas:</span>
              <span className="value">{cuadrante.CUADT_HHNOC} h</span>
            </div>
          )}
          {cuadrante.CUADT_HHNOCP !== undefined && cuadrante.CUADT_HHNOCP > 0 && (
            <div className="info-row">
              <span className="label">Horas Nocturnas P:</span>
              <span className="value">{cuadrante.CUADT_HHNOCP} h</span>
            </div>
          )}
          {cuadrante.CUADT_NOCFEST !== undefined && cuadrante.CUADT_NOCFEST > 0 && (
            <div className="info-row">
              <span className="label">Noches Festivas:</span>
              <span className="value">{cuadrante.CUADT_NOCFEST}</span>
            </div>
          )}
        </div>

        {cuadrante.CUADT_CERRADO && (
          <div className="info-section estado">
            <h3>📊 Estado</h3>
            <div className="info-row">
              <span className="label">Cerrado:</span>
              <span className="value badge-cerrado">Sí</span>
            </div>
            <div className="info-row">
              <span className="label">Fecha Cierre:</span>
              <span className="value">{formatearFecha(cuadrante.CUADT_CERRADO)}</span>
            </div>
          </div>
        )}
      </div>

      {cuadrante.idReg && (
        <div className="info-footer">
          <small>ID Registro: {cuadrante.idReg}</small>
        </div>
      )}
    </div>
  );

  if (!cuadrantes || cuadrantes.length === 0) {
    return (
      <div className="display-cuadrante empty">
        <p>No se encontraron cuadrantes</p>
      </div>
    );
  }

  // Filtrar por año y mes si están especificados
  let cuadrantesFiltrados = cuadrantes;
  if (año || mes) {
    cuadrantesFiltrados = cuadrantes.filter(c => {
      // Buscar en ambos tipos de campos (CUADT_* y CUADD_*)
      const añoDelCuadrante = c.CUADT_CAL || c.CUADD_CAL;
      const mesDelCuadrante = c.CUADT_MES || c.CUADD_MES;
      
      const cumpleAño = !año || añoDelCuadrante === año || añoDelCuadrante === año.toString() || parseInt(añoDelCuadrante) === parseInt(año);
      const cumpleMes = !mes || mesDelCuadrante == mes || mesDelCuadrante === parseInt(mes) || parseInt(mesDelCuadrante) === parseInt(mes);
      return cumpleAño && cumpleMes;
    });
    
    console.log('🔍 Filtrado aplicado:', {
      añoFiltro: año,
      mesFiltro: mes,
      totalAntes: cuadrantes.length,
      totalDespues: cuadrantesFiltrados.length
    });
  }

  if (tipoConsulta === 'persona') {
    const agrupado = agruparPorPersona();
    
    // Usar cuadrantesFiltrados en lugar de cuadrantes
    const cuadrantesParaMostrar = cuadrantesFiltrados;
    
    return (
      <div className="display-cuadrante">
        <div className="resultados-header">
          <h2>📊 Resultados de la Búsqueda</h2>
          <p>Se encontraron {cuadrantesParaMostrar.length} cuadrante(s)</p>
          
          {/* Toggle para cambiar vista */}
          <div className="vista-toggle">
            <button
              className={`toggle-btn ${vistaActual === 'grid' ? 'active' : ''}`}
              onClick={() => setVistaActual('grid')}
            >
              📊 Vista DataGrid
            </button>
            <button
              className={`toggle-btn ${vistaActual === 'lista' ? 'active' : ''}`}
              onClick={() => setVistaActual('lista')}
            >
              📋 Vista Lista
            </button>
          </div>
        </div>

        {/* Vista de cuadrícula */}
        {vistaActual === 'grid' && año && mes && (
          <CuadranteDataGrid 
            cuadrantes={cuadrantesParaMostrar} 
            año={parseInt(año)} 
            mes={parseInt(mes)}
            tipoConsulta="persona"
          />
        )}

        {/* Vista de lista */}
        {vistaActual === 'lista' && (
          <div className="vista-lista">

        {Object.entries(agrupado).filter(([persona, cuadrantesPersona]) => {
          // Filtrar las personas para mostrar solo las que tienen cuadrantes filtrados
          const cuadrantesFiltradosPersona = cuadrantesPersona.filter(c => 
            cuadrantesFiltrados.includes(c)
          );
          return cuadrantesFiltradosPersona.length > 0;
        }).map(([persona, cuadrantesPersona]) => {
          // Filtrar cuadrantes de esta persona
          const cuadrantesFiltradosPersona = cuadrantesPersona.filter(c => 
            cuadrantesFiltrados.includes(c)
          );
          
          return (
          <div key={persona} className="persona-group">
            <div className="persona-header">
              <h3>
                {cuadrantesFiltradosPersona[0].VSSPER_NOM 
                  ? `${cuadrantesFiltradosPersona[0].VSSPER_NOM} ${cuadrantesFiltradosPersona[0].VSSPER_APELL1} ${cuadrantesFiltradosPersona[0].VSSPER_APELL2}`
                  : `Persona ${persona}`
                }
              </h3>
              <span className="badge">{cuadrantesFiltradosPersona.length} cuadrante(s)</span>
            </div>

            <div className="cuadrantes-lista">
              {cuadrantesFiltradosPersona.map((cuadrante, index) => (
                <div key={index} className="cuadrante-item">
                  <div className="cuadrante-info">
                    <div className="info-principal">
                      <strong>{formatearMes(cuadrante.CUADT_MES)} {cuadrante.CUADT_CAL}</strong>
                      <span className="separador">•</span>
                      <span>Contrato: {cuadrante.CUADT_CTRT}/{cuadrante.CUADT_SERV}</span>
                      <span className="separador">•</span>
                      <span>Turno: {cuadrante.CUADT_TURNOPER}</span>
                      <span className="separador">•</span>
                      <span className="horas">{cuadrante.CUADT_HHTOT || 0}h</span>
                    </div>
                  </div>
                  <div className="cuadrante-actions">
                    <button
                      className="btn-ver"
                      onClick={() => setSelectedCuadrante(cuadrante)}
                    >
                      👁️ Ver
                    </button>
                    <button
                      className="btn-pdf"
                      onClick={() => {
                        setSelectedCuadrante(cuadrante);
                        setTimeout(() => exportarAPDF(cuadrante), 500);
                      }}
                      disabled={exportando}
                    >
                      {exportando ? '⏳ Exportando...' : '📄 PDF'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          );
        })}
        </div>
        )}

        {selectedCuadrante && (
          <div className="modal-overlay" onClick={() => setSelectedCuadrante(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Detalle del Cuadrante</h2>
                <button 
                  className="btn-close"
                  onClick={() => setSelectedCuadrante(null)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                {renderCuadranteDetalle(selectedCuadrante)}
              </div>
              <div className="modal-footer">
                <button
                  className="btn-pdf-modal"
                  onClick={() => exportarAPDF(selectedCuadrante)}
                  disabled={exportando}
                >
                  {exportando ? '⏳ Exportando...' : '📄 Descargar PDF'}
                </button>
                <button
                  className="btn-cerrar-modal"
                  onClick={() => setSelectedCuadrante(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  } else {
    // Vista por contrato/servicio
    const cuadrantesParaMostrar = cuadrantesFiltrados;
    
    return (
      <div className="display-cuadrante">
        <div className="resultados-header">
          <h2>📊 Cuadrante del Servicio</h2>
          <p>Se encontraron {cuadrantesParaMostrar.length} entrada(s)</p>
          
          {/* Toggle para cambiar vista */}
          <div className="vista-toggle">
            <button
              className={`toggle-btn ${vistaActual === 'grid' ? 'active' : ''}`}
              onClick={() => setVistaActual('grid')}
            >
              📊 Vista DataGrid
            </button>
            <button
              className={`toggle-btn ${vistaActual === 'lista' ? 'active' : ''}`}
              onClick={() => setVistaActual('lista')}
            >
              📋 Vista Lista
            </button>
          </div>
        </div>

        {/* Vista de cuadrícula */}
        {vistaActual === 'grid' && año && mes && (
          <CuadranteDataGrid 
            cuadrantes={cuadrantesParaMostrar} 
            año={parseInt(año)} 
            mes={parseInt(mes)}
            tipoConsulta="contrato"
          />
        )}

        {/* Vista de lista */}
        {vistaActual === 'lista' && (
        <div className="cuadrantes-lista">
          {cuadrantesParaMostrar.map((cuadrante, index) => (
            <div key={index} className="cuadrante-item">
              <div className="cuadrante-info">
                <div className="info-principal">
                  <strong>
                    {cuadrante.VSSPER_NOM 
                      ? `${cuadrante.VSSPER_NOM} ${cuadrante.VSSPER_APELL1} ${cuadrante.VSSPER_APELL2}`
                      : `Persona ${cuadrante.CUADT_PERSONA}`
                    }
                  </strong>
                  <span className="separador">•</span>
                  <span>Turno: {cuadrante.CUADT_TURNOPER}</span>
                  <span className="separador">•</span>
                  <span className="horas">{cuadrante.CUADT_HHTOT || 0}h</span>
                </div>
              </div>
              <div className="cuadrante-actions">
                <button
                  className="btn-ver"
                  onClick={() => setSelectedCuadrante(cuadrante)}
                >
                  👁️ Ver
                </button>
                <button
                  className="btn-pdf"
                  onClick={() => {
                    setSelectedCuadrante(cuadrante);
                    setTimeout(() => exportarAPDF(cuadrante), 500);
                  }}
                  disabled={exportando}
                >
                  {exportando ? '⏳ Exportando...' : '📄 PDF'}
                </button>
              </div>
            </div>
          ))}
        </div>
        )}

        {selectedCuadrante && (
          <div className="modal-overlay" onClick={() => setSelectedCuadrante(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Detalle del Cuadrante</h2>
                <button 
                  className="btn-close"
                  onClick={() => setSelectedCuadrante(null)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                {renderCuadranteDetalle(selectedCuadrante)}
              </div>
              <div className="modal-footer">
                <button
                  className="btn-pdf-modal"
                  onClick={() => exportarAPDF(selectedCuadrante)}
                  disabled={exportando}
                >
                  {exportando ? '⏳ Exportando...' : '📄 Descargar PDF'}
                </button>
                <button
                  className="btn-cerrar-modal"
                  onClick={() => setSelectedCuadrante(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default DisplayCuadrante;

